import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Link, useParams } from 'react-router-dom'
import { apiFetch, ApiError } from '../lib/api'
import { openDocumentReader } from '../lib/documentReader'
import { buildFieldDiffs } from '../lib/approveDiff'
import { buildPendingClaimAdvisories, type PendingClaim } from '../lib/pendingClaims'
import { humanizeEnum } from '../lib/labels'
import type { TaskQueueItem } from '../lib/tasks'
import { clsx } from 'clsx'
import {
  Check,
  X,
  FileText,
  AlertTriangle,
  GitMerge,
  Pencil,
  Link2,
  ArrowLeft,
  Landmark,
  ChevronDown,
} from 'lucide-react'
import ChainGraph from '../components/fund/ChainGraph'
import { useRegisterEnabled } from '../v2/register/api'
import ExcerptStrips, { type ExcerptSource } from '../components/pdf/ExcerptStrips'

/** Gap-1 record diff: who the update target IS (holder — instrument — issuer),
 *  read live server-side; see live_values.target_identity_for. */
interface TargetIdentity {
  kind: 'position' | 'instrument'
  position_id?: string
  holder_entity_name?: string | null
  instrument_name?: string | null
  issuer_entity_name?: string | null
  position_role?: string | null
  sibling_leg?: { position_id: string; position_role: string; units_held: unknown } | null
}

/** The record row an officer resolution flag points at (backend
 *  aggregation._attach_resolution_candidates). record_state labels staging
 *  residue — a phantom offered as a merge target must say so. */
interface ResolutionCandidate {
  kind: 'instrument' | 'entity'
  id: string
  label: string
  record_state: string
}

/** One instrument the proposed instrument's ISSUER already has in the record
 *  (backend aggregation._attach_issuer_instrument_options). Offered on EVERY
 *  instrument create, not only flagged ones — Ale ruling 2026-07-26: a broad
 *  difference ("equity participation" vs "Namenaktien") is hard to judge
 *  automatically and obvious to an officer, so the officer always gets the
 *  chance to resolve it. Ranked by the engine's own judgment (#965), NOT
 *  filtered by the plausibility floor. */
interface IssuerInstrumentOption extends ResolutionCandidate {
  kind: 'instrument'
  issuer_entity_name?: string | null
  similarity?: number
  /** Also in the resolver's question box — rendered once, not twice. */
  flagged?: boolean
}

interface AggregatedObject {
  identity: string
  kind: string
  proposal_type: string
  label: string
  facts: Record<string, unknown>
  source_language?: string | null
  confidence?: number | null
  reasoning?: string | null
  conflict_flags?: unknown[] | null
  resolution_candidate?: ResolutionCandidate | null
  /* The whole ranked candidate box, best-first, nominee at the head. One
   * fixed target means "keep separate" is the only safe answer (demo dry run
   * 2026-07-25, P8 day 7 — the nominee was wrong 6/6 and unchangeable). */
  resolution_candidates?: ResolutionCandidate[] | null
  /* Ruling A (2026-07-26): the issuer's WHOLE record set, ranked, on every
   * instrument create — flagged or not. `_total` is the count before the
   * server cap, so the list is never silently truncated. */
  issuer_instrument_options?: IssuerInstrumentOption[] | null
  issuer_instrument_option_total?: number | null
  status: string
  canonical_proposal_id: string
  canonical_group_id?: string
  proposal_ids: string[]
  documents: { document_id: string; filename: string }[]
  name_variants: string[]
  order: number
  chain_id: string
  chain_label: string
  merge_candidates: { identity: string; label: string }[]
  /** Live before-values of the update target row (updates only). */
  current_values?: Record<string, unknown> | null
  /** WI-8 advisory: other pending documents claiming the same fields. */
  pending_claims?: PendingClaim[] | null
  target_identity?: TargetIdentity | null
}

/** The role-labeled holder/instrument/issuer cells (Ale design rule: the
 *  triple is NEVER an unlabeled dash-chain). Prefers the live
 *  target_identity; creates fall back to the proposed name fields. */
function identityTriple(obj: AggregatedObject): { role: string; value: string }[] | null {
  const s = (v: unknown) => (typeof v === 'string' && v ? v : null)
  const ti = obj.target_identity
  const cells: { role: string; value: string | null }[] = ti
    ? [
        { role: 'Holder', value: s(ti.holder_entity_name) },
        { role: 'Instrument', value: s(ti.instrument_name) },
        { role: 'Issuer', value: s(ti.issuer_entity_name) },
      ]
    : ['Position', 'Trade', 'Instrument'].includes(obj.kind)
      ? [
          { role: 'Holder', value: s(obj.facts['holder_entity_name']) },
          { role: 'Instrument', value: s(obj.facts['instrument_name']) },
          { role: 'Issuer', value: s(obj.facts['issuer_entity_name']) },
        ]
      : []
  const present = cells.filter((c): c is { role: string; value: string } => c.value !== null)
  return present.length > 0 ? present : null
}

// task_data shape for a per-chain proposal_review task. The backend stores
// the chain's object dedup_keys in `identities`; we filter the aggregated
// objects down to this set so the page shows only that one chain.
interface ChainTaskData {
  identities: string[]
  chain_label?: string
  object_count?: number
  document_ids?: string[]
}

function readChainTaskData(data: TaskQueueItem['task_data']): ChainTaskData | null {
  if (!data || typeof data !== 'object') return null
  const ids = (data as Record<string, unknown>).identities
  if (!Array.isArray(ids)) return null
  const identities = ids.filter((x): x is string => typeof x === 'string')
  const rawLabel = (data as Record<string, unknown>).chain_label
  return {
    identities,
    chain_label: typeof rawLabel === 'string' ? rawLabel : undefined,
  }
}

const HIDDEN_FACT_KEYS = new Set([
  // Engineering metadata a reviewer must never see as record facts
  // (atlas review 2026-07-27): the proposal's internal routing fields
  // and the fictitious-instrument doctrine flag.
  'update_kind',
  'target_kind',
  'is_fictitious',
  // Duplicate-candidate ids: the SAME candidates are already offered by
  // name in the merge affordance below, so the raw uuid row was pure
  // leakage (atlas 2026-07-28).
  'plausible_duplicate_instrument_ids',
  'possible_duplicate_of_instrument_id',
  // "Extracted <field>" restates the value the diff row above already shows
  // (Jurisdiction: Switzerland → Zug, then Extracted Jurisdiction: Zug) —
  // a dual-state display the officer-UI principles rule out (atlas p7).
  'extracted_jurisdiction',
  'extracted_entity_nature',
  'extracted_legal_form',
  'extracted_outstanding_units',
  'extracted_units_held',
  'extracted_name',
  'source_coordinate',
  'source_language', // surfaced as its own chip, not a raw fact row
  'dedup_key',
  'depositary_id',
  'source',
  'relevance',
  'entity_id',
  'evidence_instructions',
  'holding_type',
  'issuer_entity_id',
  'holder_entity_id',
  'instrument_id',
  'position_id',
  'pledged_to_entity_id',
  'units_currency',
])
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function factLabel(k: string): string {
  return k.replace(/_/g, ' ')
}
/** Keys whose numeric values are IDENTIFIERS, not quantities: a
 *  Kataster-Nr. rendered "1,873" reads as a number the officer can compare
 *  (atlas p4, 2026-07-28). */
const IDENTIFIER_KEY = /(^|_)(code|id|ids|nr|no|number|registry)$/i

function factValue(v: unknown, key?: string): string {
  const isIdentifier = !!key && IDENTIFIER_KEY.test(key)
  if (v === null || v === undefined || v === '') return '—'
  if (Array.isArray(v)) return v.length === 0 ? '—' : v.map((x) => factValue(x, key)).join('; ')
  if (typeof v === 'object')
    return Object.entries(v as Record<string, unknown>)
      .map(([k, val]) => `${factLabel(k)}: ${factValue(val, k)}`)
      .join(', ')
  // Officer rendering (atlas review 2026-07-27): booleans as words,
  // numbers thousands-grouped, the fictitious-instrument doctrine word
  // never shown raw.
  if (typeof v === 'boolean') return v ? 'yes' : 'no'
  const str = String(v)
  if (str === 'true') return 'yes'
  if (str === 'false') return 'no'
  if (str === 'fictitious') return 'structural (system-generated)'
  if (!isIdentifier && /^-?\d+(\.\d+)?$/.test(str)) {
    const n = Number(str)
    if (Number.isFinite(n) && Math.abs(n) >= 1000)
      return n.toLocaleString('en-US', { maximumFractionDigits: 6 })
  }
  // Humanize snake_case enum values (legal_person → "legal person"); names
  // and other strings pass through untouched.
  return humanizeEnum(str)
}
function isFact(k: string, v: unknown): boolean {
  if (HIDDEN_FACT_KEYS.has(k)) return false
  if (k.endsWith('_id') && typeof v === 'string' && UUID_RE.test(v)) return false
  return true
}

const KIND_ORDER = ['Fund', 'Entity', 'Instrument', 'Position', 'Trade']

// Fund-root detection: the materialized-fund proposal. proposal_type is the
// strongest signal (`fund_materialization`); fall back to a planned/fund flag
// or a "Fund" kind label.
function isFundRoot(o: AggregatedObject): boolean {
  const pt = (o.proposal_type || '').toLowerCase()
  if (pt.includes('fund')) return true
  if (o.kind === 'Fund') return true
  return false
}

function stripExt(name: string): string {
  const i = name.lastIndexOf('.')
  return i > 0 ? name.slice(0, i) : name
}

// Provenance title for a card of peer groups (Ale 2026-07-31): pre-linkage no
// component's name outranks another's, so the card is titled by the documents
// it reviews. Mirrors the backend task title (proposal_review_task._docs_title).
function docsTitle(objs: AggregatedObject[]): string {
  const names = [
    ...new Set(objs.flatMap((o) => o.documents.map((d) => stripExt(d.filename)).filter(Boolean))),
  ].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
  if (names.length === 0) return 'Review — new documents'
  const shown = names.length <= 4 ? names : [...names.slice(0, 3), `+${names.length - 3} more`]
  return `Review — from ${shown.join(', ')}`
}

function nonEmptyFactCount(o: AggregatedObject): number {
  return Object.entries(o.facts).filter(
    ([k, v]) =>
      isFact(k, v) &&
      v !== null &&
      v !== undefined &&
      v !== '' &&
      !(Array.isArray(v) && v.length === 0)
  ).length
}

function CompareMergePanel({
  variants,
  busy,
  onMergeMany,
  onClose,
}: {
  variants: AggregatedObject[]
  busy: boolean
  onMergeMany: (keeper: AggregatedObject, others: AggregatedObject[]) => void
  onClose: () => void
}) {
  // default keeper: the variant with the most non-empty facts
  const defaultKeeper = useMemo(() => {
    let best = variants[0]
    let bestCount = -1
    for (const v of variants) {
      const c = nonEmptyFactCount(v)
      if (c > bestCount) {
        bestCount = c
        best = v
      }
    }
    return best.identity
  }, [variants])
  const [keeperId, setKeeperId] = useState(defaultKeeper)

  // union of all fact field names across variants, preserving first-seen order
  const fieldNames = useMemo(() => {
    const seen: string[] = []
    const set = new Set<string>()
    for (const v of variants) {
      for (const [k, val] of Object.entries(v.facts)) {
        if (isFact(k, val) && !set.has(k)) {
          set.add(k)
          seen.push(k)
        }
      }
    }
    return seen
  }, [variants])

  // per-field rendered value for each variant; "—" if missing
  const valueFor = (o: AggregatedObject, field: string): string => {
    if (!(field in o.facts)) return '—'
    return factValue(o.facts[field])
  }
  const fieldDiffers = (field: string): boolean => {
    const vals = variants.map((v) => valueFor(v, field))
    return new Set(vals).size > 1
  }

  const keeper = variants.find((v) => v.identity === keeperId) || variants[0]
  const others = variants.filter((v) => v.identity !== keeper.identity)

  return (
    <div className="mt-2 border border-amber-200 rounded-lg bg-amber-50/60 p-3">
      <div className="flex items-center gap-2 mb-2">
        <GitMerge size={13} className="text-amber-700" />
        <span className="text-xs font-semibold text-amber-800">
          Compare &amp; merge — {variants.length} variants
        </span>
        <span className="flex-1" />
        <span className="text-[10px] text-amber-700">
          Pick the one to keep, then merge the rest into it.
        </span>
      </div>
      <div className="overflow-x-auto">
        <table
          className="w-full border-collapse text-xs"
          style={{ minWidth: variants.length * 160 }}
        >
          <thead>
            <tr>
              <th className="text-left align-top p-1.5 w-32 text-slate-400 font-medium">Field</th>
              {variants.map((v) => {
                const isKeeper = v.identity === keeper.identity
                return (
                  <th
                    key={v.identity}
                    className={clsx(
                      'text-left align-top p-1.5 border-l border-amber-200',
                      isKeeper ? 'bg-emerald-50' : ''
                    )}
                  >
                    <label className="flex items-start gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="keeper"
                        checked={isKeeper}
                        onChange={() => setKeeperId(v.identity)}
                        disabled={busy}
                        className="mt-0.5 accent-emerald-600"
                      />
                      <span>
                        <span className="block text-[11px] font-semibold text-slate-800">
                          {v.label}
                        </span>
                        <span
                          className={clsx(
                            'block text-[9px] uppercase tracking-wide',
                            isKeeper ? 'text-emerald-700 font-semibold' : 'text-slate-400'
                          )}
                        >
                          {isKeeper ? 'Keep this' : 'Merge in'}
                        </span>
                      </span>
                    </label>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {fieldNames.map((field) => {
              const differs = fieldDiffers(field)
              return (
                <tr key={field} className="border-t border-amber-100">
                  <td className="align-top p-1.5 text-slate-400 capitalize">{factLabel(field)}</td>
                  {variants.map((v) => {
                    const val = valueFor(v, field)
                    return (
                      <td
                        key={v.identity}
                        className={clsx(
                          'align-top p-1.5 border-l border-amber-100 font-data text-slate-800',
                          differs && val !== '—' ? 'bg-amber-100' : ''
                        )}
                      >
                        {val}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
            {fieldNames.length === 0 && (
              <tr>
                <td colSpan={variants.length + 1} className="p-2 text-slate-400 italic">
                  No comparable facts.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button
          onClick={() => onMergeMany(keeper, others)}
          disabled={busy || others.length === 0}
          className="flex items-center gap-1 text-[11px] px-3 py-1 rounded font-semibold bg-nolos-primary text-white hover:opacity-90 disabled:opacity-40"
        >
          <GitMerge size={11} /> Merge into one
        </button>
        <button
          onClick={onClose}
          disabled={busy}
          className="text-[11px] px-3 py-1 rounded font-medium bg-white border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-40"
        >
          Cancel
        </button>
        <span className="text-[10px] text-slate-500">
          Keeping <span className="font-semibold text-slate-700">{keeper.label}</span>
          {others.length > 0 && (
            <>
              {' '}
              · merging {others.length} other{others.length === 1 ? '' : 's'} in
            </>
          )}
        </span>
      </div>
    </div>
  )
}

/** Officer resolution flags on an aggregated object (workbench parity with
 *  the v1 event review — officer-POV audit finding 1 / value gap 1). */
function resolutionFlagsOf(obj: AggregatedObject): string[] {
  return (obj.conflict_flags ?? []).filter((f): f is string => typeof f === 'string')
}

/**
 * The duplicate/merge question, on the officer's ACTUAL surface. Same
 * semantics as the v1 EventCard MergeResolvePanel (bias-to-separate,
 * commit 4978c7e): the resolver could not rule out that this create
 * duplicates an existing record row, so it was minted separate and
 * flagged — the officer decides. Until the question is answered the
 * card's plain Approve stays disabled (the guard this panel exists for:
 * an officer must not be able to Approve a flagged group without seeing
 * the flag).
 *   - Merge into existing → POST /objects/merge onto the record row. Since
 *     the entity analogue of the G3 merge landed (Ale ruling 2026-07-26)
 *     this works for a `possible_match` ENTITY too: before it, an officer
 *     who recognised the company could only keep it separate (minting a
 *     duplicate company) or reject (discarding the document's claims).
 *   - Keep separate → arms Approve; approving records the two names as
 *     distinct (the backend persists an alias refusal so the mention
 *     never silently auto-folds onto that candidate again).
 *
 * On an INSTRUMENT the offered list is the resolver's ranked box FOLLOWED BY
 * the rest of the issuer's record set (ruling A) — the question box is what
 * the engine wondered about; the ruling says the officer sees everything the
 * issuer has either way.
 */
function MergeQuestionPanel({
  obj,
  busy,
  keptSeparate,
  onKeepSeparate,
  onMergeIntoRecord,
}: {
  obj: AggregatedObject
  busy: boolean
  keptSeparate: boolean
  onKeepSeparate: () => void
  onMergeIntoRecord?: (
    targetId: string,
    kind: 'instrument' | 'entity',
    canonicalName?: string
  ) => void
}) {
  const flags = resolutionFlagsOf(obj)
  const isInstrument = flags.includes('pending_instrument_merge')
  const head = obj.resolution_candidate ?? null
  const candidates = useMemo(() => {
    const box: ResolutionCandidate[] = obj.resolution_candidates?.length
      ? obj.resolution_candidates
      : head
        ? [head]
        : []
    // Ruling A: the whole issuer set rides behind the question box. Members
    // already in the box keep their box position (the resolver's nominee
    // must stay the default the officer sees).
    const seen = new Set(box.map((c) => c.id))
    const rest = (obj.issuer_instrument_options ?? []).filter((o) => !seen.has(o.id))
    return [...box, ...rest]
  }, [obj.resolution_candidates, obj.issuer_instrument_options, head])
  // Rows the SERVER capped away — the issuer set it counted minus the slice
  // it sent. Added to the offered count so the "showing N of M" note is
  // honest about a fan-out issuer rather than about the box.
  const cappedAway = Math.max(
    0,
    (obj.issuer_instrument_option_total ?? 0) - (obj.issuer_instrument_options?.length ?? 0)
  )
  const [targetId, setTargetId] = useState<string | null>(candidates[0]?.id ?? null)
  const candidate = candidates.find((c) => c.id === targetId) ?? candidates[0] ?? null
  const candidateIsStaging = candidate != null && candidate.record_state !== 'confirmed'
  const canMerge = candidate != null && onMergeIntoRecord != null
  // D-1 canonical-name election (Ale ruling 2026-07-24): the officer picks
  // which name survives the merge. Default keeps the record's name. The
  // document-side name must be the exact extracted name — entity_name on the
  // entity arm (ruling B), instrument_name on the instrument arm.
  const documentName = documentNameOf(obj)
  const [nameChoice, setNameChoice] = useState<'existing' | 'document'>('existing')
  return (
    <div
      data-testid="merge-resolve-panel"
      className="mb-2.5 bg-amber-50/60 border border-amber-200 rounded-md p-3"
    >
      <p className="text-xs font-semibold text-amber-900 mb-1 flex items-center gap-1.5">
        <GitMerge size={12} /> {isInstrument ? 'Possible duplicate instrument' : 'Possible match'}
      </p>
      <p className="text-xs text-slate-600 mb-2.5">
        <span className="font-medium text-slate-800">{obj.label}</span>{' '}
        {candidate ? (
          <>
            may already exist in the record as{' '}
            <span className="font-medium text-slate-800" data-testid="merge-candidate">
              {candidate.label}
            </span>
            .
          </>
        ) : (
          <>may already exist in the record.</>
        )}{' '}
        It was kept separate — you decide.
      </p>
      {candidateIsStaging && (
        <p
          data-testid="staging-candidate-warning"
          className="text-[11px] text-amber-800 bg-amber-100 rounded px-2 py-1 mb-2.5"
        >
          This candidate is not on the confirmed record yet — it is still awaiting review. Merging
          into it would attach confirmed claims to something unconfirmed.
        </p>
      )}
      {candidates.length > 1 && (
        <TargetChoiceList
          testId="merge-target-choice"
          objIdentity={obj.identity}
          options={candidates}
          selectedId={candidate?.id ?? null}
          onSelect={setTargetId}
          disabled={busy || keptSeparate}
          // What `total` means to TargetChoiceList is "how many exist",
          // against a list that is the question box PLUS the issuer set. The
          // server total counts only the issuer set, so the box-only members
          // must be added back — otherwise a box candidate the issuer scan
          // never returned makes options.length exceed total and the "showing
          // N of M" note fires with M < N.
          total={candidates.length + cappedAway}
          heading={
            isInstrument
              ? 'Merge into which record instrument? (closest match first)'
              : 'Merge into which record company? (closest match first)'
          }
          footnote={
            isInstrument
              ? 'None of these? Keep separate — it becomes its own instrument.'
              : 'None of these? Keep separate — it becomes its own company.'
          }
        />
      )}
      {canMerge && (
        <div className="mb-2.5 text-[11px] text-slate-600" data-testid="merge-name-choice">
          <span className="font-medium text-slate-700 mr-2">Name to keep on merge:</span>
          <label className="mr-3 inline-flex items-center gap-1">
            <input
              type="radio"
              name={`merge-name-${obj.identity}`}
              checked={nameChoice === 'existing'}
              onChange={() => setNameChoice('existing')}
              data-testid="merge-name-existing"
            />
            {candidate ? <>&ldquo;{candidate.label}&rdquo; (existing)</> : <>the existing name</>}
          </label>
          <label className="inline-flex items-center gap-1">
            <input
              type="radio"
              name={`merge-name-${obj.identity}`}
              checked={nameChoice === 'document'}
              onChange={() => setNameChoice('document')}
              data-testid="merge-name-document"
            />
            &ldquo;{documentName}&rdquo; (this document)
          </label>
          <span className="block text-[10.5px] text-slate-400 mt-0.5">
            The losing name is kept as a known alias either way.
          </span>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="merge-accept"
          onClick={() =>
            candidate &&
            onMergeIntoRecord?.(
              candidate.id,
              candidate.kind,
              nameChoice === 'document' ? documentName : undefined
            )
          }
          disabled={!canMerge || busy || keptSeparate}
          title={
            canMerge
              ? `Fold this ${isInstrument ? 'instrument' : 'company'} onto the existing record row — nothing new is created`
              : `No mergeable record ${isInstrument ? 'instrument' : 'company'} was identified`
          }
          className="inline-flex items-center gap-1 text-[11px] px-3 py-1.5 rounded-md bg-amber-600 text-white font-medium hover:bg-amber-700 transition-colors disabled:opacity-50"
        >
          <GitMerge size={11} /> Merge into existing
        </button>
        <button
          type="button"
          data-testid="merge-keep-separate"
          onClick={onKeepSeparate}
          disabled={busy || keptSeparate}
          className="text-[11px] px-3 py-1.5 rounded-md bg-white border border-slate-200 text-slate-600 font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
        >
          Keep separate
        </button>
        {keptSeparate && (
          <span className="text-[11px] text-slate-500" data-testid="merge-kept-separate-note">
            {isInstrument
              ? 'Kept separate — Approve creates it as its own instrument.'
              : 'Kept separate — Approve records the two names as distinct.'}
          </span>
        )}
      </div>
    </div>
  )
}

/** The extracted name this document proposes — the "document" arm of the D-1
 *  canonical-name election. */
function documentNameOf(obj: AggregatedObject): string {
  const key = obj.proposal_type.endsWith('_entity') ? 'entity_name' : 'instrument_name'
  const v = obj.facts[key]
  return typeof v === 'string' && v ? v : obj.label
}

/** Ranked radio list of merge targets, shared by the flagged question panel
 *  and the standing issuer affordance (ruling A).
 *
 *  Volume discipline (Ale ruling 2026-07-26, "the UI must stay usable"): the
 *  ranked head is shown; the rest is one click away, never dropped. When the
 *  server capped the list, the cap is STATED — a silently truncated list is
 *  the failure mode the ruling exists to prevent. */
function TargetChoiceList({
  testId,
  objIdentity,
  options,
  selectedId,
  onSelect,
  disabled,
  heading,
  footnote,
  total,
  initiallyVisible = 5,
}: {
  testId: string
  objIdentity: string
  options: (ResolutionCandidate & { issuer_entity_name?: string | null; flagged?: boolean })[]
  selectedId: string | null
  onSelect: (id: string) => void
  disabled: boolean
  heading: string
  footnote: string
  total: number
  initiallyVisible?: number
}) {
  const [expanded, setExpanded] = useState(false)
  const [filter, setFilter] = useState('')
  const needle = filter.trim().toLowerCase()
  const matching = needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options
  // The selected row is ALWAYS rendered, even when it falls outside the head
  // or the filter — a radio group whose checked member is invisible reads as
  // "nothing selected" and the officer loses their own choice.
  const head = expanded || needle ? matching : matching.slice(0, initiallyVisible)
  const selected = options.find((o) => o.id === selectedId)
  const shown = head.some((o) => o.id === selectedId) || !selected ? head : [selected, ...head]
  // Count against what is actually ON SCREEN, not against `head` — when the
  // selected row is prepended from outside the head, "Show all N" would
  // otherwise claim one more hidden row than there is (and could offer to
  // expand a list with nothing left to reveal).
  const shownIds = new Set(shown.map((o) => o.id))
  const hidden = matching.filter((o) => !shownIds.has(o.id)).length
  return (
    <div className="mb-2.5 text-[11px] text-slate-600" data-testid={testId}>
      <span className="font-medium text-slate-700 block mb-1">{heading}</span>
      {options.length > 8 && (
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by name…"
          disabled={disabled}
          data-testid={`${testId}-filter`}
          className="mb-1 w-full max-w-xs rounded border border-slate-200 px-2 py-1 text-[11px]"
        />
      )}
      {shown.map((c) => (
        <label key={c.id} className="flex items-center gap-1.5 py-0.5">
          <input
            type="radio"
            name={`merge-target-${objIdentity}`}
            checked={selectedId === c.id}
            onChange={() => onSelect(c.id)}
            disabled={disabled}
            data-testid={`merge-target-${c.id}`}
          />
          <span className="text-slate-700">{c.label}</span>
          {c.record_state !== 'confirmed' && (
            <span className="text-[10px] text-amber-700">(unconfirmed)</span>
          )}
          {c.flagged && (
            <span className="text-[10px] text-amber-700">(resolver flagged this one)</span>
          )}
        </label>
      ))}
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          data-testid={`${testId}-show-all`}
          className="mt-0.5 text-[10.5px] text-sky-700 underline"
        >
          Show all {matching.length}
        </button>
      )}
      {total > options.length && (
        <span
          className="block text-[10.5px] text-amber-700 mt-0.5"
          data-testid={`${testId}-capped`}
        >
          Showing the {options.length} closest of {total} — filter to narrow.
        </span>
      )}
      <span className="block text-[10.5px] text-slate-400 mt-0.5">{footnote}</span>
    </div>
  )
}

/**
 * The STANDING merge affordance (Ale ruling 2026-07-26) — shown on EVERY
 * instrument create, not only the ones the resolver flagged.
 *
 * Why it exists: a broad difference between two names for one thing ("equity
 * participation" vs "Namenaktien") is hard for the engine to judge and
 * obvious to an officer. Waiting for the engine to raise a question means the
 * officer never gets asked in exactly the cases where they are the better
 * judge. So the issuer's whole record set is on screen, ranked, every time.
 *
 * Deliberately quiet, not amber: nothing is WRONG here and nothing is being
 * asked. The default is "create as new" — plain Approve behaves exactly as it
 * did before this panel existed, which is why the pack scripts' answers (and
 * therefore their pins) are unmoved.
 */
function IssuerInstrumentPanel({
  obj,
  busy,
  onMergeIntoRecord,
}: {
  obj: AggregatedObject
  busy: boolean
  onMergeIntoRecord?: (
    targetId: string,
    kind: 'instrument' | 'entity',
    canonicalName?: string
  ) => void
}) {
  const options = obj.issuer_instrument_options ?? []
  const total = obj.issuer_instrument_option_total ?? options.length
  const [targetId, setTargetId] = useState<string | null>(null)
  const [nameChoice, setNameChoice] = useState<'existing' | 'document'>('existing')
  const selected = options.find((o) => o.id === targetId) ?? null
  const issuer = options[0]?.issuer_entity_name ?? obj.facts['issuer_entity_name']
  const issuerLabel = typeof issuer === 'string' && issuer ? issuer : 'this issuer'
  const documentName = documentNameOf(obj)
  if (options.length === 0) return null
  return (
    <div
      data-testid="issuer-instrument-panel"
      className="mb-2.5 bg-slate-50 border border-slate-200 rounded-md p-3"
    >
      <p className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
        <GitMerge size={12} /> {issuerLabel} already has {total} instrument
        {total === 1 ? '' : 's'} in the record
      </p>
      <p className="text-xs text-slate-600 mb-2.5">
        <span className="font-medium text-slate-800">{obj.label}</span> will be created as a NEW
        instrument unless you link it to one of them.
      </p>
      <TargetChoiceList
        testId="issuer-instrument-choice"
        objIdentity={obj.identity}
        options={options}
        selectedId={targetId}
        onSelect={setTargetId}
        disabled={busy}
        total={total}
        heading="Is it one of these? (closest match first)"
        footnote="Leave unselected to create it as a new instrument — that is what Approve does."
      />
      {selected && (
        <div className="mb-2.5 text-[11px] text-slate-600" data-testid="issuer-name-choice">
          <span className="font-medium text-slate-700 mr-2">Name to keep on link:</span>
          <label className="mr-3 inline-flex items-center gap-1">
            <input
              type="radio"
              name={`issuer-name-${obj.identity}`}
              checked={nameChoice === 'existing'}
              onChange={() => setNameChoice('existing')}
              data-testid="issuer-name-existing"
            />
            &ldquo;{selected.label}&rdquo; (existing)
          </label>
          <label className="inline-flex items-center gap-1">
            <input
              type="radio"
              name={`issuer-name-${obj.identity}`}
              checked={nameChoice === 'document'}
              onChange={() => setNameChoice('document')}
              data-testid="issuer-name-document"
            />
            &ldquo;{documentName}&rdquo; (this document)
          </label>
          <span className="block text-[10.5px] text-slate-400 mt-0.5">
            The losing name is kept as a known alias either way.
          </span>
        </div>
      )}
      <button
        type="button"
        data-testid="issuer-instrument-link"
        onClick={() =>
          selected &&
          onMergeIntoRecord?.(
            selected.id,
            'instrument',
            nameChoice === 'document' ? documentName : undefined
          )
        }
        disabled={!selected || busy || onMergeIntoRecord == null}
        title={
          selected
            ? 'Link this document’s claims onto the existing instrument — nothing new is created'
            : 'Pick an existing instrument first'
        }
        className="inline-flex items-center gap-1 text-[11px] px-3 py-1.5 rounded-md bg-slate-700 text-white font-medium hover:bg-slate-800 transition-colors disabled:opacity-50"
      >
        <GitMerge size={11} /> Link to existing
      </button>
    </div>
  )
}

// The page the object was extracted from — so a doc chip opens the PDF at the
// cited page (verify the claim at source). From source_coordinate.page or source_page.
function sourcePageOf(o: AggregatedObject): number | undefined {
  const sc = o.facts['source_coordinate'] as { page?: unknown } | undefined
  if (sc && typeof sc.page === 'number') return sc.page
  const p = o.facts['source_page']
  return typeof p === 'number' ? p : undefined
}

// Verify-at-source P1: the cited passage(s) behind this object, for the
// focused card's excerpt strips. Today the extractor captures ONE
// source_coordinate per proposal (§A); the aggregation's canonical document
// is where it points.
function excerptSourcesOf(o: AggregatedObject): ExcerptSource[] {
  const d = o.documents[0]
  if (!d) return []
  const sc = o.facts['source_coordinate'] as { page?: unknown; text?: unknown } | undefined
  return [
    {
      documentId: d.document_id,
      filename: d.filename,
      page: sourcePageOf(o),
      text: sc && typeof sc.text === 'string' && sc.text ? sc.text : undefined,
      label: '§A',
    },
  ]
}

function ObjectCard({
  obj,
  busy,
  canConnect,
  onDecide,
  onEditApprove,
  onMergeMany,
  onMergeIntoRecord,
  onKeepSeparateRecord,
  onConnect,
  holderOptions,
  onOpenDoc,
  activeDocId,
  objectsByIdentity,
  onMarkFundVehicle,
  focused,
  onFocus,
}: {
  obj: AggregatedObject
  busy: boolean
  canConnect: boolean
  onDecide: (action: 'approve' | 'reject', justification?: string) => void
  onEditApprove: (
    editedFields: Record<string, { original: string; corrected: string }>,
    editJustification?: string
  ) => void
  onMergeMany: (keeper: AggregatedObject, others: AggregatedObject[]) => void
  /** Fold this create onto an existing record row — the G3 /objects/merge
   *  variant for instruments, its entity analogue (ruling B) for companies.
   *  Reached from the flagged question panel AND from the standing issuer
   *  affordance (ruling A). */
  onMergeIntoRecord?: (
    targetId: string,
    kind: 'instrument' | 'entity',
    canonicalName?: string
  ) => void
  /** RK W2 (Ale 2026-08-08): the EXPLICIT distinct answer. Calls
   *  POST /objects/keep-separate — writes the durable alias refusal and
   *  clears the gate flag server-side. Approve alone no longer answers the
   *  question (it 409s while the flag is open). */
  onKeepSeparateRecord?: () => void
  onConnect: (holderIdentity: string) => void
  holderOptions: { identity: string; label: string }[]
  onOpenDoc: (documentId: string, page?: number) => void
  activeDocId: string | null
  objectsByIdentity: Map<string, AggregatedObject>
  /** Gap-6: materialize a create_entity proposal as the fund vehicle. */
  onMarkFundVehicle?: () => void
  /** Verify-at-source P1 (focus-follows): exactly one card is expanded with
   *  its source excerpts; the rest fold to one-liners. */
  focused: boolean
  onFocus: () => void
}) {
  const [rejecting, setRejecting] = useState(false)
  const [justification, setJustification] = useState('')
  const [editing, setEditing] = useState(false)
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  // D-2 (Ale ruling 2026-07-24): Save & approve with changed fields is an
  // officer-authored deviation — it carries a typed reason into the trail
  // exactly like Reject's justification. An edit session that changes
  // nothing stays a plain single-click approve.
  const [editJustification, setEditJustification] = useState('')
  const [connecting, setConnecting] = useState(false)
  const [holderId, setHolderId] = useState('')
  const [comparing, setComparing] = useState(false)
  // Officer resolution flag (finding 1): the duplicate/merge question must
  // be answered — Merge, or an explicit Keep separate — before plain
  // Approve unlocks. keptSeparate is the officer's on-card acknowledgement.
  const [keptSeparate, setKeptSeparate] = useState(false)
  const resolutionFlags = resolutionFlagsOf(obj)
  const hasMergeQuestion =
    resolutionFlags.includes('pending_instrument_merge') ||
    resolutionFlags.includes('possible_match')
  const approveGuarded = hasMergeQuestion && !keptSeparate
  const facts = Object.entries(obj.facts).filter(([k, v]) => isFact(k, v))
  const showConnect = canConnect && obj.kind === 'Entity'

  // Gap-1 record diff: current → proposed per changed field, live record
  // first (current_values), builder snapshot (previous_*) as fallback.
  // Creates have neither → no diff rows, single-column as before.
  const diffByField = useMemo(
    () => new Map(buildFieldDiffs(obj.facts, obj.current_values).map((d) => [d.field, d])),
    [obj]
  )
  // The previous_*/existing_* snapshot rows duplicate the diff's old side —
  // hide them wherever a diff row covers the base field.
  const displayFacts = facts.filter(([k]) => {
    const m = /^(?:previous|existing)_(.+)$/.exec(k)
    return !(m && diffByField.has(m[1]))
  })
  const identityCells = useMemo(() => identityTriple(obj), [obj])
  const advisories = useMemo(() => buildPendingClaimAdvisories(obj.pending_claims), [obj])
  const excerptSources = useMemo(() => excerptSourcesOf(obj), [obj])

  // D-3c residual (Ale ruling 2026-07-24): supersession/conflict cards show
  // WHEN each side speaks from, prominently — balance updates resolve by
  // recency, so the as-of dates are the decision-bearing fact. Copy only.
  const str = (v: unknown) => (typeof v === 'string' && v ? v : null)
  const conflictFlagged = (obj.conflict_flags ?? []).some(
    (f) => typeof f === 'string' && (f.includes('conflict') || f.includes('contradiction'))
  )
  const isSupersession = diffByField.size > 0 || conflictFlagged
  const docAsOf =
    str(obj.facts['effective_date']) ?? str(obj.facts['as_of']) ?? str(obj.facts['balance_as_of'])
  const recordAsOf =
    str(obj.current_values?.['effective_date']) ?? str(obj.facts['previous_effective_date'])

  // this object + its merge candidates, resolved to full objects from current state
  const compareVariants = useMemo(() => {
    const out = [obj]
    for (const c of obj.merge_candidates || []) {
      const full = objectsByIdentity.get(c.identity)
      if (full && full.identity !== obj.identity) out.push(full)
    }
    return out
  }, [obj, objectsByIdentity])
  const compareCount = compareVariants.length

  const startEdit = () => {
    const init: Record<string, string> = {}
    for (const [k, v] of facts) init[k] = factValue(v, k)
    setDrafts(init)
    setRejecting(false)
    setConnecting(false)
    setEditJustification('')
    setEditing(true)
  }
  // Live count of fields the officer actually changed — the reason is only
  // required when the approval deviates from what the document proposed.
  const editedFieldCount = editing
    ? facts.filter(([k, v]) => (drafts[k] ?? factValue(v, k)) !== factValue(v, k)).length
    : 0
  const editReasonMissing = editedFieldCount > 0 && !editJustification.trim()
  const saveEdit = () => {
    const edited: Record<string, { original: string; corrected: string }> = {}
    for (const [k, v] of facts) {
      const original = factValue(v, k)
      const corrected = drafts[k] ?? original
      if (corrected !== original) edited[k] = { original, corrected }
    }
    onEditApprove(edited, Object.keys(edited).length > 0 ? editJustification.trim() : undefined)
    setEditing(false)
  }

  // Folded one-liner (focus-follows): everything the officer needs to pick
  // this card next — label, the role triple, open questions — but no verbs.
  // Deciding requires focusing, and focusing puts the source excerpts in
  // front of the decision (the record can be wrong; an approved record
  // has to be right).
  if (!focused) {
    return (
      <button
        type="button"
        id={`card-${obj.identity}`}
        data-testid="object-card-collapsed"
        onClick={onFocus}
        className="w-full text-left bg-white border border-slate-200 rounded-lg px-4 py-2 flex items-center gap-2.5 hover:border-nolos-primary/60 transition-colors"
        style={{ boxShadow: 'var(--shadow-card)' }}
      >
        <span className="text-xs font-semibold text-slate-900 truncate shrink-0">{obj.label}</span>
        {identityCells && (
          <span className="text-[10px] text-slate-400 truncate">
            {identityCells.map((c) => c.value).join(' — ')}
          </span>
        )}
        <span className="flex-1" />
        {hasMergeQuestion && (
          <span className="text-[10px] font-medium text-amber-700 bg-amber-50 rounded px-1.5 py-0.5 shrink-0">
            duplicate question
          </span>
        )}
        <span className="text-[10px] text-slate-400 shrink-0">
          {obj.documents.length} doc{obj.documents.length === 1 ? '' : 's'}
        </span>
        <ChevronDown size={12} className="text-slate-300 shrink-0" />
      </button>
    )
  }

  return (
    <div
      id={`card-${obj.identity}`}
      className={clsx(
        'bg-white border rounded-lg transition-shadow',
        'border-nolos-primary/50 ring-1 ring-nolos-primary/20'
      )}
      style={{ boxShadow: 'var(--shadow-card)' }}
    >
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100">
        <span className="text-xs font-semibold text-slate-900">{obj.label}</span>
        {obj.confidence != null && (
          <span className="text-[10px] text-slate-400 font-data">
            {Math.round(obj.confidence * 100)}%
          </span>
        )}
        <span className="flex-1" />
        {!editing && (
          <>
            <button
              onClick={() => onDecide('approve')}
              disabled={busy || approveGuarded}
              data-testid="object-approve"
              title={
                approveGuarded
                  ? 'Answer the duplicate question below first — merge into the existing record row, or keep it separate.'
                  : undefined
              }
              className="flex items-center gap-1 text-[11px] px-2 py-1 rounded font-medium bg-emerald-600 text-white hover:opacity-90 disabled:opacity-40"
            >
              <Check size={11} /> Approve
            </button>
            <button
              onClick={startEdit}
              disabled={busy || approveGuarded}
              title={
                approveGuarded
                  ? 'Answer the duplicate question below first — Save & approve is an approval too.'
                  : undefined
              }
              className="flex items-center gap-1 text-[11px] px-2 py-1 rounded font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-40"
            >
              <Pencil size={11} /> Edit
            </button>
            {/* kind === 'Fund' means the funds table already has this entity
                as a fund vehicle (aggregation join, Fix A dossier 2026-07-31)
                — offering to mark it again is the p8 day-1 stale button. */}
            {obj.proposal_type === 'create_entity' &&
              obj.kind !== 'Fund' &&
              obj.canonical_group_id &&
              onMarkFundVehicle && (
                <button
                  onClick={onMarkFundVehicle}
                  disabled={busy}
                  data-testid="mark-fund-vehicle"
                  title="Make this entity the Fund itself — the top of the ownership chain. Use it when the fund vehicle was not recognised automatically."
                  className="flex items-center gap-1 text-[11px] px-2 py-1 rounded font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-40"
                >
                  <Landmark size={11} /> Mark as fund
                </button>
              )}
            {showConnect && (
              <button
                onClick={() => {
                  setConnecting((c) => !c)
                  setRejecting(false)
                }}
                disabled={busy}
                className={clsx(
                  'flex items-center gap-1 text-[11px] px-2 py-1 rounded font-medium disabled:opacity-40',
                  connecting
                    ? 'bg-nolos-primary text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                )}
              >
                <Link2 size={11} /> Connect
              </button>
            )}
            <button
              onClick={() => {
                setRejecting((r) => !r)
                setConnecting(false)
              }}
              disabled={busy}
              className={clsx(
                'flex items-center gap-1 text-[11px] px-2 py-1 rounded font-medium disabled:opacity-40',
                rejecting
                  ? 'bg-red-600 text-white'
                  : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              )}
            >
              <X size={11} /> Discard
            </button>
          </>
        )}
        {editing && (
          <>
            <button
              onClick={saveEdit}
              disabled={busy || editReasonMissing}
              data-testid="save-approve"
              title={
                editReasonMissing
                  ? 'You changed the proposed values — say why below. The reason lands in the audit trail like a reject justification.'
                  : undefined
              }
              className="flex items-center gap-1 text-[11px] px-2 py-1 rounded font-medium bg-emerald-600 text-white hover:opacity-90 disabled:opacity-40"
            >
              <Check size={11} /> Save &amp; approve
            </button>
            <button
              onClick={() => setEditing(false)}
              disabled={busy}
              className="flex items-center gap-1 text-[11px] px-2 py-1 rounded font-medium bg-slate-100 text-slate-500 hover:bg-slate-200 disabled:opacity-40"
            >
              Cancel
            </button>
          </>
        )}
      </div>
      <div className="px-4 py-3">
        {/* Verify-at-source: the cited passage renders in the same moment as
            the proposal — the officer verifies without leaving the card. */}
        {!editing && excerptSources.length > 0 && (
          <div className="mb-3">
            <ExcerptStrips sources={excerptSources} onOpenDoc={onOpenDoc} />
          </div>
        )}
        {!editing && hasMergeQuestion && (
          <MergeQuestionPanel
            obj={obj}
            busy={busy}
            keptSeparate={keptSeparate}
            onKeepSeparate={() => {
              // RK W2: the answer must land server-side — the flag stays
              // open (and Approve 409s) until the durable refusal writes.
              setKeptSeparate(true)
              onKeepSeparateRecord?.()
            }}
            onMergeIntoRecord={onMergeIntoRecord}
          />
        )}
        {/* Ruling A: the standing affordance. Only when the resolver did NOT
            flag — a flagged create already carries the issuer's whole set
            inside the question panel, and two lists would be two answers. */}
        {!editing && !hasMergeQuestion && obj.proposal_type === 'create_instrument' && (
          <IssuerInstrumentPanel obj={obj} busy={busy} onMergeIntoRecord={onMergeIntoRecord} />
        )}
        {identityCells && (
          <div
            data-testid="identity-cells"
            className="mb-2.5 flex flex-wrap gap-x-5 gap-y-1.5 rounded border border-slate-200 bg-slate-50 px-2.5 py-1.5"
          >
            {identityCells.map((c) => (
              <div key={c.role}>
                <div className="text-[9px] font-medium uppercase tracking-wide text-slate-400">
                  {c.role}
                </div>
                <div className="text-xs font-medium text-slate-800">{c.value}</div>
              </div>
            ))}
          </div>
        )}
        {!editing && isSupersession && (docAsOf || recordAsOf) && (
          <div
            data-testid="as-of-strip"
            className="mb-2.5 flex flex-wrap gap-x-5 gap-y-1.5 rounded border border-amber-200 bg-amber-50 px-2.5 py-1.5"
          >
            <div>
              <div className="text-[9px] font-medium uppercase tracking-wide text-amber-700">
                This document speaks as of
              </div>
              <div className="text-xs font-semibold text-slate-800 font-data">
                {docAsOf ?? 'no date given'}
              </div>
            </div>
            <div>
              <div className="text-[9px] font-medium uppercase tracking-wide text-amber-700">
                Current record as of
              </div>
              <div className="text-xs font-semibold text-slate-800 font-data">
                {recordAsOf ?? 'no date on record'}
              </div>
            </div>
          </div>
        )}
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-xs">
          {(editing ? facts : displayFacts).map(([k, v]) => {
            const diff = editing ? undefined : diffByField.get(k)
            return (
              <div key={k} className="contents">
                <dt className="text-slate-400 capitalize">{factLabel(k)}</dt>
                {editing ? (
                  <dd>
                    <input
                      value={drafts[k] ?? ''}
                      onChange={(e) => setDrafts((d) => ({ ...d, [k]: e.target.value }))}
                      className="w-full text-xs font-data border border-slate-300 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-nolos-primary"
                    />
                  </dd>
                ) : diff ? (
                  <dd className="text-slate-800 font-data" data-testid="field-diff">
                    <span className="text-slate-400 line-through decoration-slate-300">
                      {factValue(diff.old, k)}
                    </span>
                    <span className="mx-1.5 text-slate-400">→</span>
                    <span className="font-semibold">{factValue(v, k)}</span>
                  </dd>
                ) : (
                  <dd className="text-slate-800 font-data">{factValue(v, k)}</dd>
                )}
              </div>
            )
          })}
        </dl>
        {editing && editedFieldCount > 0 && (
          <div className="mt-2" data-testid="edit-reason-block">
            <textarea
              value={editJustification}
              onChange={(e) => setEditJustification(e.target.value)}
              placeholder={`Why are you changing ${editedFieldCount === 1 ? 'this value' : 'these values'}? (required)`}
              data-testid="edit-reason"
              className="w-full text-xs border border-amber-300 rounded p-2 focus:outline-none focus:ring-1 focus:ring-amber-400"
              rows={2}
            />
            <p className="mt-0.5 text-[10px] text-slate-400">
              {editedFieldCount} field{editedFieldCount === 1 ? '' : 's'} changed — your reason is
              recorded in the audit trail with the approval.
            </p>
          </div>
        )}
        {!editing && advisories.length > 0 && (
          <div
            data-testid="pending-claims-strip"
            className="mt-2 text-xs text-amber-700 bg-amber-50 rounded px-2 py-1.5"
          >
            <div className="flex items-start gap-1.5">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              <span className="font-medium">
                {obj.pending_claims!.length} other unconfirmed claim
                {obj.pending_claims!.length === 1 ? '' : 's'} on this object
              </span>
            </div>
            {advisories.map((a) => (
              <div key={a.field} className="mt-1 pl-[18px]">
                <span className="capitalize">{factLabel(a.field)}</span>
                {': '}
                {a.claims
                  .map(
                    (c) =>
                      `${factValue(c.value, a.field)} (${c.documentFilename ?? 'unknown document'})`
                  )
                  .join(' / ')}
              </div>
            ))}
          </div>
        )}
        {obj.source_language && obj.source_language !== 'en' && (
          <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 rounded px-2 py-1.5">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            <span>{obj.source_language.toUpperCase()} source — verify figures</span>
          </div>
        )}
        {obj.name_variants.length > 1 && (
          <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 rounded px-2 py-1.5">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            <span>Also seen as: {obj.name_variants.join(' · ')}</span>
          </div>
        )}
        {obj.merge_candidates?.length > 0 && compareCount > 1 && !comparing && (
          <div className="mt-2">
            <button
              onClick={() => {
                setComparing(true)
                setRejecting(false)
                setConnecting(false)
              }}
              disabled={busy}
              className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-40"
              title="Compare these variants side by side and merge them into one"
            >
              <GitMerge size={12} /> Compare &amp; merge ({compareCount})
            </button>
          </div>
        )}
        {comparing && compareCount > 1 && (
          <CompareMergePanel
            variants={compareVariants}
            busy={busy}
            onMergeMany={onMergeMany}
            onClose={() => setComparing(false)}
          />
        )}
        {obj.reasoning && <p className="mt-2 text-xs text-slate-500 italic">{obj.reasoning}</p>}
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] text-slate-400">
            seen in {obj.documents.length} document{obj.documents.length === 1 ? '' : 's'}:
          </span>
          {obj.documents.map((d) => (
            <button
              key={d.document_id}
              onClick={() => onOpenDoc(d.document_id, sourcePageOf(obj))}
              title={`${d.filename}${sourcePageOf(obj) ? ` — opens at p. ${sourcePageOf(obj)}` : ''}`}
              className={clsx(
                'inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border',
                activeDocId === d.document_id
                  ? 'border-nolos-primary text-nolos-primary bg-blue-50'
                  : 'border-slate-200 text-slate-500 hover:bg-slate-50'
              )}
            >
              <FileText size={10} />
              {d.filename.replace(/\.pdf$/i, '').slice(0, 28)}
            </button>
          ))}
        </div>
        {rejecting && (
          <div className="mt-2">
            <textarea
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder="Why is this discarded? (required)"
              className="w-full text-xs border border-red-200 rounded p-2 focus:outline-none focus:ring-1 focus:ring-red-400"
              rows={2}
            />
            <button
              onClick={() => onDecide('reject', justification.trim())}
              disabled={busy || !justification.trim()}
              className="mt-1 text-[11px] font-semibold px-3 py-1 rounded text-white bg-red-600 hover:opacity-90 disabled:opacity-40"
            >
              Confirm discard
            </button>
          </div>
        )}
        {connecting && showConnect && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5 bg-blue-50 rounded px-2 py-2">
            <span className="text-xs text-slate-600">Connect under holder:</span>
            <select
              value={holderId}
              onChange={(e) => setHolderId(e.target.value)}
              className="text-xs border border-slate-300 rounded px-1.5 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-nolos-primary"
            >
              <option value="">Choose an entity…</option>
              {holderOptions.map((h) => (
                <option key={h.identity} value={h.identity}>
                  {h.label}
                </option>
              ))}
            </select>
            <button
              onClick={() => onConnect(holderId)}
              disabled={busy || !holderId}
              className="text-[11px] font-semibold px-3 py-1 rounded text-white bg-nolos-primary hover:opacity-90 disabled:opacity-40"
            >
              Connect
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default function ChainReviewPage({
  taskId: taskIdProp,
  embedded = false,
  onResolved,
}: {
  /** Scope to a chain task. Falls back to the :taskId route param (standalone). */
  taskId?: string
  /** Rendered inside the v2 cockpit pane — drop the standalone page chrome. */
  embedded?: boolean
  /** Called when the chain is fully cleared (cockpit advances the queue). */
  onResolved?: () => void
} = {}) {
  const { taskId: taskIdParam } = useParams<{ taskId?: string }>()
  const taskId = taskIdProp ?? taskIdParam
  // Demo-v2 W1: while the fund register is on, the founding verb is retired
  // from this pane (register is the only door). null-while-probing keeps the
  // pill hidden until the probe answers, so no stale click window opens.
  const registerOn = useRegisterEnabled() !== false
  const [objects, setObjects] = useState<AggregatedObject[] | null>(null)
  const [chainTask, setChainTask] = useState<ChainTaskData | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [activeDocId, setActiveDocId] = useState<string | null>(null)
  // Verify-at-source P1 (focus-follows): one focused card, the rest folded.
  // orderedRef mirrors the render order (filled after peerGroups below) so
  // advanceFocus and j/k can walk it without re-creating callbacks.
  const orderedRef = useRef<string[]>([])
  const [focusedId, setFocusedId] = useState<string | null>(null)

  // A decision retires the card — focus moves to the next one in reading
  // order (Approve advances the review) BEFORE the reload drops it.
  const advanceFocus = useCallback((from: string) => {
    const order = orderedRef.current
    const i = order.indexOf(from)
    const next = order[i + 1] ?? order[i - 1] ?? null
    setFocusedId(next)
    if (next)
      document.getElementById(`card-${next}`)?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      })
  }, [])

  // j/k walk the fold like a queue: next/previous card gets the focus (and
  // with it, its excerpts). Ignored while typing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key !== 'j' && e.key !== 'k') return
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
      const order = orderedRef.current
      if (order.length === 0) return
      setFocusedId((prev) => {
        const cur = prev && order.includes(prev) ? order.indexOf(prev) : 0
        const nxt = e.key === 'j' ? Math.min(order.length - 1, cur + 1) : Math.max(0, cur - 1)
        const id = order[nxt]
        document.getElementById(`card-${id}`)?.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        })
        return id
      })
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
  const [flash, setFlash] = useState<string | null>(null)
  // The diagram and the proposal cards are alternative views of the same chain
  // (not stacked). Default to the cards — that's where the officer acts.
  const [view, setView] = useState<'cards' | 'chart'>('cards')
  // Entity ids already in the record (backend-confirmed) — drives the
  // proposed-vs-record distinction in the diagram.
  const [recordIds, setRecordIds] = useState<Set<string>>(new Set())

  const fetchObjects = useCallback(async () => {
    try {
      const d = await apiFetch<{
        objects: AggregatedObject[]
        record_entity_ids?: string[]
      }>('/api/proposals/aggregated')
      setObjects(d.objects)
      setRecordIds(new Set(d.record_entity_ids ?? []))
    } catch {
      setObjects([])
    }
  }, [])

  useEffect(() => {
    fetchObjects()
  }, [fetchObjects])

  // When opened from a per-chain queue task (/review/:taskId), load the task
  // so we can scope the aggregated objects to its `identities` set.
  useEffect(() => {
    if (!taskId) {
      setChainTask(null)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const t = await apiFetch<TaskQueueItem>(`/api/tasks/${encodeURIComponent(taskId)}`)
        if (!cancelled) setChainTask(readChainTaskData(t.task_data))
      } catch {
        if (!cancelled) setChainTask(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [taskId])

  // Identity set the page is scoped to (null = all-chains / plain /review).
  const scopeIdentities = useMemo(
    () => (chainTask ? new Set(chainTask.identities) : null),
    [chainTask]
  )
  // Aggregated objects filtered to the scope, if any. Re-derived after every
  // reload so the chain shrinks naturally as its objects are approved.
  const scopedObjects = useMemo(() => {
    if (!objects) return objects
    if (!scopeIdentities) return objects
    return objects.filter((o) => scopeIdentities.has(o.identity))
  }, [objects, scopeIdentities])

  // Open the source document in its own browser tab (not an in-app pane), so
  // the review page keeps the full width for the cards/diagram.
  const openDoc = useCallback((documentId: string, page?: number) => {
    setActiveDocId(documentId)
    openDocumentReader(documentId, {
      page,
      returnTo: window.location.pathname + window.location.search,
    })
  }, [])

  // Gap-6: materialize a create_entity proposal as the fund vehicle (chain
  // head) when auto-classification missed it — the v1 EventCard affordance,
  // now reachable from the officer front door. Idempotent server-side.
  const markFundVehicle = useCallback(
    async (obj: AggregatedObject) => {
      if (!obj.canonical_group_id) return
      setBusyId(obj.identity)
      try {
        const r = await apiFetch<{ fund_id: string; fund_name: string; created: boolean }>(
          `/api/proposals/${obj.canonical_group_id}/proposals/${obj.canonical_proposal_id}/mark-fund-vehicle`,
          { method: 'POST', body: JSON.stringify({}) }
        )
        setFlash(
          r.created
            ? `${r.fund_name} materialized as a fund — it now heads its chain.`
            : `${r.fund_name} already exists as a fund — nothing new created.`
        )
        await fetchObjects()
      } catch {
        setFlash(`Could not mark ${obj.label} as a fund vehicle.`)
      } finally {
        setBusyId(null)
      }
    },
    [fetchObjects]
  )

  const decide = useCallback(
    async (obj: AggregatedObject, action: 'approve' | 'reject', justification?: string) => {
      setBusyId(obj.identity)
      try {
        await apiFetch('/api/proposals/objects/confirm', {
          method: 'POST',
          body: JSON.stringify({
            proposal_ids: obj.proposal_ids,
            action,
            ...(action === 'reject' ? { rejection_justification: justification } : {}),
          }),
        })
        // Approve said what it did; reject only said "rejected", so the
        // officer never learned that discarding writes nothing and keeps
        // their reason on the trail (atlas review 2026-07-28).
        setFlash(
          action === 'approve'
            ? `${obj.label} approved — written to the record.`
            : `${obj.label} discarded — nothing was written to the record, and your reason is on the audit trail.`
        )
        advanceFocus(obj.identity)
        await fetchObjects()
      } catch (e) {
        // Never swallow the backend's reason — a bare "could not" is the
        // blank-error pattern this train has been removing everywhere.
        const detail =
          e instanceof ApiError
            ? ((e.body as { detail?: unknown } | undefined)?.detail ??
              `${e.statusText} (${e.status})`)
            : e instanceof Error
              ? e.message
              : null
        setFlash(
          `Could not ${action === 'approve' ? 'approve' : 'discard'} ${obj.label}${
            typeof detail === 'string' && detail ? ` — ${detail}` : '.'
          }`
        )
      } finally {
        setBusyId(null)
      }
    },
    [fetchObjects, advanceFocus]
  )

  const editApprove = useCallback(
    async (
      obj: AggregatedObject,
      editedFields: Record<string, { original: string; corrected: string }>,
      editJustification?: string
    ) => {
      setBusyId(obj.identity)
      try {
        await apiFetch('/api/proposals/objects/confirm', {
          method: 'POST',
          body: JSON.stringify({
            proposal_ids: obj.proposal_ids,
            action: 'approve',
            ...(Object.keys(editedFields).length > 0 ? { edited_fields: editedFields } : {}),
            // D-2: the typed reason for the deviation — trails like a reject
            // justification.
            ...(editJustification ? { edit_justification: editJustification } : {}),
          }),
        })
        const n = Object.keys(editedFields).length
        setFlash(
          `${obj.label} approved — written to the record${n > 0 ? ` (${n} field${n === 1 ? '' : 's'} corrected)` : ''}.`
        )
        advanceFocus(obj.identity)
        await fetchObjects()
      } catch {
        setFlash(`Could not approve ${obj.label}.`)
      } finally {
        setBusyId(null)
      }
    },
    [fetchObjects, advanceFocus]
  )

  const connect = useCallback(
    async (assetObj: AggregatedObject, holderIdentity: string) => {
      const holderObj = (scopedObjects || []).find((o) => o.identity === holderIdentity)
      if (!holderObj) {
        setFlash('Could not find the holder to connect under.')
        return
      }
      setBusyId(assetObj.identity)
      try {
        await apiFetch('/api/proposals/objects/connect', {
          method: 'POST',
          body: JSON.stringify({
            asset_proposal_ids: assetObj.proposal_ids,
            holder_proposal_ids: holderObj.proposal_ids,
          }),
        })
        setFlash(
          `Connected ${assetObj.label} under ${holderObj.label} — fictitious instrument + position generated.`
        )
        await fetchObjects()
      } catch {
        setFlash(`Could not connect ${assetObj.label} under ${holderObj.label}.`)
      } finally {
        setBusyId(null)
      }
    },
    [scopedObjects, fetchObjects]
  )

  // Finding 1 (2026-07-24): accept a pending_instrument_merge from the
  // workbench pane — fold the flagged create onto the record instrument the
  // resolver pointed at (same sanctioned /objects/merge G3 variant the v1
  // event review uses). The source create flips to superseded, so the next
  // reload drops the card and the chain shrinks naturally.
  const mergeIntoRecord = useCallback(
    async (
      obj: AggregatedObject,
      targetId: string,
      kind: 'instrument' | 'entity',
      canonicalName?: string
    ) => {
      setBusyId(obj.identity)
      const noun = kind === 'entity' ? 'company' : 'instrument'
      try {
        await apiFetch('/api/proposals/objects/merge', {
          method: 'POST',
          body: JSON.stringify({
            source_proposal_ids: obj.proposal_ids,
            // Ruling B (2026-07-26): the entity arm is the same verb with the
            // other target — "this proposed company IS that company".
            ...(kind === 'entity'
              ? { target_entity_id: targetId }
              : { target_instrument_id: targetId }),
            // D-1 canonical-name election (Ale 2026-07-24): present only
            // when the officer picked the document's name as survivor.
            ...(canonicalName ? { canonical_name: canonicalName } : {}),
          }),
        })
        const target =
          [...(obj.resolution_candidates ?? []), ...(obj.issuer_instrument_options ?? [])].find(
            (c) => c.id === targetId
          )?.label ??
          obj.resolution_candidate?.label ??
          `the existing ${noun}`
        setFlash(
          `${obj.label} merged into ${target} — nothing new is created; this document's claims land on the existing ${noun}.`
        )
        await fetchObjects()
      } catch {
        setFlash(`Could not merge ${obj.label} — it was left separate.`)
      } finally {
        setBusyId(null)
      }
    },
    [fetchObjects]
  )

  // RK W2 (Ale 2026-08-08, reverse approve-implies-distinct): "Keep
  // separate" is now an EXPLICIT server-side answer — it writes the durable
  // alias refusal and clears the gate flag via /objects/keep-separate.
  // Approve alone books only and 409s while the question is open.
  const keepSeparate = useCallback(
    async (obj: AggregatedObject) => {
      setBusyId(obj.identity)
      try {
        await apiFetch('/api/proposals/objects/keep-separate', {
          method: 'POST',
          body: JSON.stringify({ source_proposal_ids: obj.proposal_ids }),
        })
        setFlash(
          `${obj.label} kept separate — the names are recorded as distinct; Approve now books it as its own record.`
        )
        await fetchObjects()
      } catch {
        setFlash(`Could not record the keep-separate answer for ${obj.label}.`)
      } finally {
        setBusyId(null)
      }
    },
    [fetchObjects]
  )

  const mergeMany = useCallback(
    async (keeper: AggregatedObject, others: AggregatedObject[]) => {
      if (others.length === 0) return
      setBusyId(keeper.identity)
      try {
        for (const other of others) {
          await apiFetch('/api/proposals/objects/merge', {
            method: 'POST',
            body: JSON.stringify({
              source_proposal_ids: other.proposal_ids,
              target_proposal_ids: keeper.proposal_ids,
            }),
          })
        }
        setFlash(
          `Merged ${others.length} variant${others.length === 1 ? '' : 's'} into ${keeper.label}.`
        )
        await fetchObjects()
      } catch {
        setFlash(`Could not merge variants into ${keeper.label}.`)
      } finally {
        setBusyId(null)
      }
    },
    [fetchObjects]
  )

  const objectsByIdentity = useMemo(() => {
    const m = new Map<string, AggregatedObject>()
    for (const o of scopedObjects || []) m.set(o.identity, o)
    return m
  }, [scopedObjects])

  // --- Group by the connected component (chain_id), not the fund-name label ---
  // Ale 2026-07-31: pre-linkage there is NO hierarchy between components.
  // Every connected component renders as an identical peer group titled by
  // its own content — no annex, no "not yet connected" section, nothing
  // subordinated under anything. Structure appears only from evidence: a
  // component with a position/trade/fund root is a linked chain (and its
  // members lose the Connect verb); everything else is simply a peer.
  // Groups keep first-mention order across the day's documents — neutral,
  // never rank.
  const peerGroups = useMemo(() => {
    const all = scopedObjects || []
    const byId = new Map<string, AggregatedObject[]>()
    for (const o of all) {
      const arr = byId.get(o.chain_id) ?? []
      arr.push(o)
      byId.set(o.chain_id, arr)
    }

    const isRealLabel = (l: string | undefined | null): l is string =>
      !!l && l !== 'Not yet connected' && l !== 'Chain'

    type PeerGroup = {
      chainId: string
      label: string
      objects: AggregatedObject[]
      linked: boolean
      firstDoc: string
      firstIdx: number
    }

    const groups: PeerGroup[] = []
    for (const [chainId, objs] of byId) {
      const linked = objs.some(
        (o) => o.kind === 'Position' || o.kind === 'Trade' || o.kind === 'Fund' || isFundRoot(o)
      )
      // Display name: the group's own content — a real chain_label, falling
      // back to the fund/head entity, then the first object.
      const head =
        objs.find((o) => isFundRoot(o)) ||
        objs.find((o) => o.kind === 'Fund') ||
        objs.find((o) => o.kind === 'Entity')
      const label = objs.map((o) => o.chain_label).find(isRealLabel) || head?.label || objs[0].label
      const docNames = objs.flatMap((o) => o.documents.map((d) => stripExt(d.filename)))
      const firstDoc = docNames.length > 0 ? [...docNames].sort()[0].toLowerCase() : ''
      const firstIdx = Math.min(...objs.map((o) => all.indexOf(o)))
      groups.push({ chainId, label, objects: objs, linked, firstDoc, firstIdx })
    }

    groups.sort((a, b) => a.firstDoc.localeCompare(b.firstDoc) || a.firstIdx - b.firstIdx)
    return groups
  }, [scopedObjects])

  const groupByKind = useCallback((objs: AggregatedObject[]) => {
    const g: Record<string, AggregatedObject[]> = {}
    for (const o of objs) (g[o.kind] ||= []).push(o)
    return g
  }, [])

  // Verify-at-source P1 (focus-follows): identities in exactly the order the
  // cards render (groups → KIND_ORDER), so focus advance and j/k walk the
  // page top to bottom.
  const orderedIdentities = useMemo(() => {
    const ids: string[] = []
    for (const g of peerGroups) {
      const grouped = groupByKind(g.objects)
      for (const kind of KIND_ORDER) for (const o of grouped[kind] ?? []) ids.push(o.identity)
    }
    return ids
  }, [peerGroups, groupByKind])
  orderedRef.current = orderedIdentities
  // Default focus: the first card. A stale focus (object decided → gone from
  // the reload) also falls back to the first remaining card.
  const focusedIdentity =
    focusedId && orderedIdentities.includes(focusedId) ? focusedId : (orderedIdentities[0] ?? null)

  const holderOptionsFor = useCallback(
    (obj: AggregatedObject) =>
      (scopedObjects || [])
        .filter((o) => o.kind === 'Entity' && o.identity !== obj.identity)
        .map((o) => ({ identity: o.identity, label: o.label })),
    [scopedObjects]
  )

  // Embedded in the cockpit: when the scoped chain empties (every object
  // approved/discarded), tell the workbench to drop this item and advance.
  useEffect(() => {
    if (!embedded || objects === null || scopeIdentities === null) return
    if ((scopedObjects || []).length === 0) onResolved?.()
  }, [embedded, objects, scopedObjects, scopeIdentities, onResolved])

  const KIND_HEADINGS: Record<string, string> = {
    Fund: 'Fund',
    Entity: 'Entities',
    Instrument: 'Instruments',
    Position: 'Positions',
    Trade: 'Trades',
  }

  if (objects === null)
    return (
      <div className="flex justify-center py-16">
        <div className="w-5 h-5 border-2 border-nolos-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  const scoped = scopeIdentities !== null
  const total = (scopedObjects || []).length
  const singleNamedChain = peerGroups.length === 1 && peerGroups[0].linked
  // Scoped header: a single evidence-linked chain keeps its name (the task's
  // chain_label, else the group's own label); a card holding peers is titled
  // by provenance — the documents under review (Ale 2026-07-31). Matches the
  // backend task title so the queue and the card say the same thing.
  const scopedTitle =
    chainTask?.chain_label ||
    (singleNamedChain
      ? peerGroups[0].label
      : total > 0
        ? docsTitle(scopedObjects || [])
        : 'Chain review')
  // Group headings are redundant only when the scoped page title is already
  // the one chain's name; peer cards always show each group's own heading.
  const showGroupHeadings = !scoped || !singleNamedChain
  return (
    <div
      className={
        embedded
          ? 'h-full overflow-y-auto pr-1 flex-1 min-w-0'
          : 'h-[calc(100vh-7rem)] overflow-y-auto pr-1'
      }
    >
      <div className="space-y-4">
        <div>
          {scoped && !embedded && (
            <Link
              to="/tasks"
              className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-nolos-primary mb-1"
            >
              <ArrowLeft size={13} /> back to queue
            </Link>
          )}
          <h2 className="text-lg font-semibold text-slate-900">
            {scoped ? scopedTitle : 'Chain review'}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {scoped ? (
              <>
                {total} object{total === 1 ? '' : 's'}{' '}
                {singleNamedChain ? 'in this chain' : 'from these documents'} awaiting review.
                Approve each individually — it is written to the record at once.
              </>
            ) : (
              <>
                {total} object{total === 1 ? '' : 's'} awaiting review, aggregated across documents.
                Approve each individually — it is written to the record at once.
              </>
            )}
          </p>
        </div>
        {total > 0 && (
          <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5 text-xs font-medium">
            <button
              type="button"
              onClick={() => setView('cards')}
              className={clsx(
                'px-3 py-1 rounded',
                view === 'cards'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              )}
            >
              Proposals
            </button>
            <button
              type="button"
              onClick={() => setView('chart')}
              className={clsx(
                'px-3 py-1 rounded',
                view === 'chart'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              )}
            >
              Diagram
            </button>
          </div>
        )}
        {flash && (
          <div className="text-xs font-medium text-emerald-700 bg-emerald-50 rounded px-3 py-2">
            {flash}
          </div>
        )}
        {total === 0 ? (
          <div className="text-center py-12 text-slate-400 text-sm">
            Nothing to review — the chain is clear.
          </div>
        ) : (
          <>
            {peerGroups.map((group) => {
              const grouped = groupByKind(group.objects)
              return (
                <div key={group.chainId} className="space-y-3">
                  {/* Every component is an identical peer group titled by its
                      own content (Ale 2026-07-31) — no annex, nothing nested
                      under anything. */}
                  {showGroupHeadings && (
                    <div className="text-sm font-semibold text-slate-900">
                      {group.label}
                      <span className="text-slate-400 font-normal">
                        {' '}
                        · {group.objects.length} object{group.objects.length === 1 ? '' : 's'}
                      </span>
                    </div>
                  )}
                  {/* Diagram view: granular chain graph — every primitive is
                      its own node; click a node for facts/state/actions/trades. */}
                  {view === 'chart' && group.objects.length > 0 && (
                    <ChainGraph
                      objects={group.objects}
                      recordIds={recordIds}
                      busyId={busyId}
                      height="calc(100vh - 14rem)"
                      onDecide={(o, a) => decide(o as AggregatedObject, a)}
                      onOpenDoc={openDoc}
                    />
                  )}
                  {/* Per-object review cards, kind-ordered. Connect is offered
                      on unlinked entities only — members of an evidence-linked
                      chain lose it (ObjectCard gates it to entities). */}
                  {view === 'cards' &&
                    KIND_ORDER.filter((k) => grouped[k]?.length).map((kind) => (
                      <div key={kind} className="space-y-2">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                          {KIND_HEADINGS[kind] || kind}
                        </h3>
                        {grouped[kind].map((obj) => (
                          <ObjectCard
                            key={obj.identity}
                            obj={obj}
                            busy={busyId === obj.identity}
                            canConnect={!group.linked}
                            onDecide={(a, j) => decide(obj, a, j)}
                            onEditApprove={(ef, ej) => editApprove(obj, ef, ej)}
                            onMergeMany={mergeMany}
                            onMergeIntoRecord={(tid, kind, name) =>
                              mergeIntoRecord(obj, tid, kind, name)
                            }
                            onKeepSeparateRecord={() => keepSeparate(obj)}
                            objectsByIdentity={objectsByIdentity}
                            onConnect={(hi) => connect(obj, hi)}
                            holderOptions={holderOptionsFor(obj)}
                            onOpenDoc={openDoc}
                            activeDocId={activeDocId}
                            onMarkFundVehicle={
                              /* Demo-v2 W1: founding verb retired while the
                               * fund register is on — the register is the
                               * only door (endpoint answers 409 anyway). */
                              registerOn ? undefined : () => markFundVehicle(obj)
                            }
                            focused={focusedIdentity === obj.identity}
                            onFocus={() => setFocusedId(obj.identity)}
                          />
                        ))}
                      </div>
                    ))}
                </div>
              )
            })}
          </>
        )}
      </div>
    </div>
  )
}
